import axiosInstance from './axiosConfig';
import { AIModelsResponse } from '../../types';

/**
 * AI モデル管理用のAPIサービス
 */
export class AIModelService {
  /**
   * 利用可能なAIモデル一覧を取得
   * @returns Promise<AIModelsResponse>
   */
  static async getAvailableModels(): Promise<AIModelsResponse> {
    try {
      const response = await axiosInstance.get('/ai-agents/models');
      return response.data;
    } catch (error) {
      console.error('モデル一覧の取得に失敗しました:', error);
      throw error;
    }
  }

  /**
   * モデル情報をキャッシュから取得（ローカルストレージ使用）
   * @returns AIModelsResponse | null
   */
  static getCachedModels(): AIModelsResponse | null {
    try {
      const cached = localStorage.getItem('ai_models_cache');
      if (cached) {
        const data = JSON.parse(cached);
        // キャッシュの有効期限をチェック（1時間）
        if (data.timestamp && Date.now() - data.timestamp < 3600000) {
          return data.models;
        }
      }
      return null;
    } catch (error) {
      console.error('キャッシュからのモデル取得に失敗しました:', error);
      return null;
    }
  }

  /**
   * モデル情報をキャッシュに保存
   * @param models AIModelsResponse
   */
  static setCachedModels(models: AIModelsResponse): void {
    try {
      const cacheData = {
        models,
        timestamp: Date.now()
      };
      localStorage.setItem('ai_models_cache', JSON.stringify(cacheData));
    } catch (error) {
      console.error('モデル情報のキャッシュに失敗しました:', error);
    }
  }

  /**
   * モデル一覧を取得（キャッシュ機能付き）
   * @param forceRefresh キャッシュを無視して最新データを取得
   * @returns Promise<AIModelsResponse>
   */
  static async getModelsWithCache(forceRefresh = false): Promise<AIModelsResponse> {
    // キャッシュから取得を試行
    if (!forceRefresh) {
      const cached = this.getCachedModels();
      if (cached) {
        return cached;
      }
    }

    // APIから最新データを取得
    const models = await this.getAvailableModels();
    
    // キャッシュに保存
    this.setCachedModels(models);
    
    return models;
  }

  /**
   * キャッシュをクリア
   */
  static clearCache(): void {
    try {
      localStorage.removeItem('ai_models_cache');
    } catch (error) {
      console.error('キャッシュのクリアに失敗しました:', error);
    }
  }
}

export default AIModelService;
